import React, { useRef, useState, useEffect, useCallback } from 'react';
import { FaArrowLeft, FaArrowRight, FaRegStar, FaSort, FaStar, FaRobot, FaTimes, FaUser, FaFilm, FaSearch } from "react-icons/fa";
import { useNavigate, useParams } from 'react-router-dom';
import { IoClose } from "react-icons/io5";
import { FiSend, FiMessageCircle } from "react-icons/fi";
import { MdMovieFilter } from "react-icons/md";
import SubNav from '../components/subNav';
import SEO from '../components/SEO';
import CastingErrorBoundary from '../components/CastingErrorBoundary';
import { toFriendlyError } from '../utils/friendlyError';
import { baseUrl } from '../config';

/* ═══════════════════════════════════════════════════════
   INJECTED STYLES — no separate CSS file needed
═══════════════════════════════════════════════════════ */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');

  :root {
    --ink:       #0d1117;
    --ink-soft:  #1c2333;
    --border:    rgba(255,255,255,0.08);
    --gold:      #c9a84c;
    --gold-pale: rgba(201,168,76,0.12);
    --gold-glow: rgba(201,168,76,0.3);
    --text:      #e8e3d6;
    --text-dim:  #8a8478;
    --indigo:    #4f6ef2;
    --indigo-soft: rgba(79,110,242,0.15);
    --red:       #e05a5a;
    --green:     #4caf7d;
    --radius:    14px;
    --shadow:    0 8px 32px rgba(0,0,0,0.45);
  }

  .ac-root { font-family: 'DM Sans', sans-serif; background: var(--ink); min-height: 100vh; color: var(--text); }
  .ac-root * { box-sizing: border-box; }

  /* ── Page Header ── */
  .ac-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 32px; border-bottom: 1px solid var(--border);
    background: rgba(13,17,23,0.85); backdrop-filter: blur(12px);
    position: sticky; top: 0; z-index: 50;
  }
  .ac-header h1 {
    font-family: 'Cormorant Garamond', serif; font-size: 1.6rem;
    font-weight: 600; color: var(--text); letter-spacing: 0.04em;
    display: flex; align-items: center; gap: 10px;
  }
  .ac-header h1 span { color: var(--gold); }
  .ac-header-actions { display: flex; align-items: center; gap: 12px; }
  .ac-search-bar {
    display: flex; align-items: center; gap: 10px;
    background: var(--ink-soft); border: 1px solid var(--border);
    border-radius: 40px; padding: 8px 16px; width: 240px;
    transition: border-color .2s;
  }
  .ac-search-bar:focus-within { border-color: var(--gold); }
  .ac-search-bar input {
    background: transparent; border: none; outline: none;
    color: var(--text); font-size: .85rem; width: 100%;
    font-family: 'DM Sans', sans-serif;
  }
  .ac-search-bar input::placeholder { color: var(--text-dim); }
  .ac-filter-btn {
    display: flex; align-items: center; gap: 8px;
    background: var(--ink-soft); border: 1px solid var(--border);
    border-radius: 8px; padding: 8px 14px; cursor: pointer;
    color: var(--text-dim); font-size: .82rem; transition: all .2s;
  }
  .ac-filter-btn:hover { border-color: var(--gold); color: var(--gold); }
  .ac-filter-dropdown {
    position: absolute; top: calc(100% + 8px); right: 0;
    background: var(--ink-soft); border: 1px solid var(--border);
    border-radius: var(--radius); padding: 8px; min-width: 200px;
    box-shadow: var(--shadow); z-index: 100;
  }
  .ac-filter-label {
    display: flex; align-items: center; gap: 8px;
    padding: 8px 10px; border-radius: 8px; cursor: pointer;
    font-size: .83rem; color: var(--text-dim); transition: all .2s;
  }
  .ac-filter-label:hover { background: var(--gold-pale); color: var(--gold); }
  .ac-filter-label input { accent-color: var(--gold); }
  .ac-view-all-btn {
    margin-top: 6px; padding: 8px 10px; border-radius: 8px;
    font-size: .82rem; color: var(--indigo); cursor: pointer; text-align: center;
  }
  .ac-view-all-btn:hover { background: var(--indigo-soft); }

  /* ── Main Layout ── */
  .ac-body {
    display: grid; grid-template-columns: 380px 1fr;
    min-height: calc(100vh - 115px); gap: 0;
  }

  /* ── LEFT PANEL (Chat) ── */
  .ac-left {
    background: var(--ink-soft); border-right: 1px solid var(--border);
    display: flex; flex-direction: column; height: calc(100vh - 115px);
    position: sticky; top: 115px;
  }
  .ac-left-header {
    padding: 20px 22px 14px;
    border-bottom: 1px solid var(--border);
    display: flex; align-items: center; gap: 12px;
  }
  .ac-left-header .icon-wrap {
    width: 38px; height: 38px; border-radius: 50%;
    background: linear-gradient(135deg, var(--gold) 0%, #e8b84b 100%);
    display: flex; align-items: center; justify-content: center;
    color: var(--ink); font-size: 1rem; flex-shrink: 0;
    box-shadow: 0 0 0 4px var(--gold-pale);
  }
  .ac-left-header-text h3 {
    font-family: 'Cormorant Garamond', serif; font-size: 1.1rem; font-weight: 600;
  }
  .ac-left-header-text p { font-size: .72rem; color: var(--text-dim); margin-top: 2px; }

  /* Chat area */
  .ac-chat-area {
    flex: 1; overflow-y: auto; padding: 16px 18px;
    display: flex; flex-direction: column; gap: 12px;
    scroll-behavior: smooth;
  }
  .ac-chat-area::-webkit-scrollbar { width: 4px; }
  .ac-chat-area::-webkit-scrollbar-track { background: transparent; }
  .ac-chat-area::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }

  .ac-msg {
    max-width: 88%; animation: fadeSlideUp .3s ease;
  }
  .ac-msg.bot { align-self: flex-start; }
  .ac-msg.user { align-self: flex-end; }

  .ac-msg-bubble {
    padding: 10px 14px; border-radius: 14px;
    font-size: .82rem; line-height: 1.55; word-break: break-word;
  }
  .ac-msg.bot .ac-msg-bubble {
    background: rgba(255,255,255,0.06); border: 1px solid var(--border);
    border-bottom-left-radius: 4px; color: var(--text);
  }
  .ac-msg.user .ac-msg-bubble {
    background: linear-gradient(135deg, var(--gold) 0%, #e8b84b 100%);
    border-bottom-right-radius: 4px; color: var(--ink); font-weight: 500;
  }

  /* Typing dots */
  .ac-typing { display: flex; gap: 4px; padding: 4px 0; }
  .ac-typing span {
    width: 6px; height: 6px; border-radius: 50%; background: var(--gold);
    animation: bounce .9s infinite;
  }
  .ac-typing span:nth-child(2) { animation-delay: .15s; }
  .ac-typing span:nth-child(3) { animation-delay: .3s; }
  @keyframes bounce { 0%,60%,100% { transform: translateY(0); } 30% { transform: translateY(-6px); } }
  @keyframes fadeSlideUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }

  /* Setup Buttons */
  .ac-choice-btns { display: flex; flex-direction: column; gap: 8px; padding: 14px 18px; border-top: 1px solid var(--border); }
  .ac-choice-btn {
    padding: 11px 16px; border-radius: 10px; cursor: pointer;
    font-size: .83rem; font-weight: 600; transition: all .2s;
    font-family: 'DM Sans', sans-serif; text-align: center;
  }
  .ac-choice-primary {
    background: linear-gradient(135deg, var(--gold) 0%, #e8b84b 100%);
    color: var(--ink); border: none;
  }
  .ac-choice-primary:hover { box-shadow: 0 4px 20px var(--gold-glow); transform: translateY(-1px); }
  .ac-choice-btn:disabled { opacity: .55; cursor: not-allowed; box-shadow: none; transform: none; }
  .ac-choice-secondary {
    background: transparent; color: var(--gold);
    border: 1.5px solid var(--gold);
  }
  .ac-choice-secondary:hover { background: var(--gold-pale); }

  /* Manual Input Form */
  .ac-manual-form { padding: 14px 18px; border-top: 1px solid var(--border); display: flex; flex-direction: column; gap: 8px; }
  .ac-input {
    background: rgba(255,255,255,0.05); border: 1px solid var(--border);
    border-radius: 8px; padding: 9px 12px; color: var(--text);
    font-size: .82rem; outline: none; width: 100%;
    font-family: 'DM Sans', sans-serif; transition: border-color .2s;
  }
  .ac-input:focus { border-color: var(--gold); }
  .ac-input::placeholder { color: var(--text-dim); }
  .ac-input-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .ac-select {
    background: rgba(255,255,255,0.05); border: 1px solid var(--border);
    border-radius: 8px; padding: 9px 12px; color: var(--text);
    font-size: .82rem; outline: none; width: 100%;
    font-family: 'DM Sans', sans-serif; cursor: pointer;
  }
  .ac-select option { background: #1c2333; color: var(--text); }
  .ac-scout-btn {
    background: linear-gradient(135deg, var(--gold) 0%, #e8b84b 100%);
    color: var(--ink); border: none; border-radius: 10px;
    padding: 11px; font-weight: 700; font-size: .85rem;
    cursor: pointer; transition: all .2s; font-family: 'DM Sans', sans-serif;
  }
  .ac-scout-btn:hover:not(:disabled) { box-shadow: 0 4px 20px var(--gold-glow); transform: translateY(-1px); }
  .ac-scout-btn:disabled { opacity: .5; cursor: not-allowed; }

  /* Sending state */
  .ac-sending-indicator {
    display: flex; align-items: center; gap: 10px;
    padding: 12px 18px; border-top: 1px solid var(--border);
    color: var(--gold); font-size: .8rem; font-weight: 500;
  }
  .ac-spin {
    width: 18px; height: 18px; border-radius: 50%;
    border: 2px solid var(--border); border-top-color: var(--gold);
    animation: spin .8s linear infinite; flex-shrink: 0;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── RIGHT PANEL (Actors) ── */
  .ac-right {
    background: var(--ink); padding: 28px 32px;
    overflow-y: auto;
  }
  .ac-section { margin-bottom: 44px; }
  .ac-section-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 24px;
  }
  .ac-section-title {
    font-family: 'Cormorant Garamond', serif; font-size: 1.35rem;
    font-weight: 600; letter-spacing: .02em;
    display: flex; align-items: center; gap: 10px;
  }
  .ac-section-title .badge {
    font-family: 'DM Sans', sans-serif; font-size: .68rem;
    background: var(--gold-pale); color: var(--gold);
    padding: 3px 8px; border-radius: 20px; font-weight: 600;
    letter-spacing: .06em; text-transform: uppercase;
  }
  .ac-section-counter {
    font-size: .78rem; color: var(--text-dim);
  }

  /* Carousel */
  .ac-carousel-wrap {
    position: relative;
  }
  .ac-carousel {
    display: flex; gap: 20px;
    overflow: hidden;
  }
  .ac-carousel-inner {
    display: flex; gap: 20px;
    transition: transform .45s cubic-bezier(.25,.46,.45,.94);
    will-change: transform;
  }

  /* Actor Card */
  .ac-card {
    flex: 0 0 calc(33.33% - 14px);
    background: var(--ink-soft); border: 1px solid var(--border);
    border-radius: 18px; overflow: hidden;
    transition: transform .25s, box-shadow .25s;
    cursor: pointer; position: relative;
  }
  .ac-card:hover { transform: translateY(-4px); box-shadow: 0 16px 40px rgba(0,0,0,.5); border-color: rgba(201,168,76,.3); }

  .ac-card-img-wrap {
    position: relative; width: 100%; aspect-ratio: 3/4; overflow: hidden;
  }
  .ac-card-img-wrap img {
    width: 100%; height: 100%; object-fit: cover;
    transition: transform .4s ease;
  }
  .ac-card:hover .ac-card-img-wrap img { transform: scale(1.04); }
  .ac-card-img-overlay {
    position: absolute; inset: 0;
    background: linear-gradient(to top, rgba(13,17,23,.95) 0%, rgba(13,17,23,.2) 55%, transparent 100%);
  }
  .ac-card-score {
    position: absolute; top: 12px; right: 12px;
    background: rgba(13,17,23,.8); backdrop-filter: blur(6px);
    border: 1px solid var(--gold); border-radius: 8px;
    padding: 4px 9px; font-size: .75rem; font-weight: 600; color: var(--gold);
  }
  .ac-card-tier {
    position: absolute; top: 12px; left: 12px;
    background: rgba(13,17,23,.7); backdrop-filter: blur(6px);
    border-radius: 6px; padding: 3px 8px; font-size: .67rem; color: var(--text-dim);
    text-transform: uppercase; letter-spacing: .05em;
  }
  .ac-card-body { padding: 16px; }
  .ac-card-name {
    font-family: 'Cormorant Garamond', serif; font-size: 1.1rem;
    font-weight: 600; margin-bottom: 6px;
  }
  .ac-card-meta { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
  .ac-card-tag {
    font-size: .68rem; padding: 3px 8px; border-radius: 20px;
    background: rgba(255,255,255,.05); color: var(--text-dim);
    border: 1px solid var(--border);
  }
  .ac-card-stars { display: flex; gap: 2px; color: var(--gold); font-size: .8rem; margin-bottom: 12px; }
  .ac-card-details {
    max-height: 0; overflow: hidden;
    transition: max-height .35s ease, opacity .35s ease;
    opacity: 0;
  }
  .ac-card-details.open { max-height: 300px; opacity: 1; }
  .ac-card-detail-row {
    display: flex; justify-content: space-between; align-items: flex-start;
    padding: 5px 0; border-bottom: 1px solid rgba(255,255,255,.04);
    font-size: .76rem;
  }
  .ac-card-detail-row:last-child { border-bottom: none; }
  .ac-card-detail-row .label { color: var(--text-dim); }
  .ac-card-detail-row .val { color: var(--text); text-align: right; max-width: 58%; }
  .ac-card-actions { display: flex; gap: 8px; margin-top: 14px; }
  .ac-btn-view {
    flex: 1; padding: 9px; border-radius: 8px; border: 1px solid var(--border);
    background: transparent; color: var(--text-dim); font-size: .78rem;
    cursor: pointer; transition: all .2s; font-family: 'DM Sans', sans-serif;
  }
  .ac-btn-view:hover { border-color: var(--text-dim); color: var(--text); }
  .ac-btn-contact {
    flex: 1; padding: 9px; border-radius: 8px; border: none;
    background: linear-gradient(135deg, var(--gold) 0%, #e8b84b 100%);
    color: var(--ink); font-size: .78rem; font-weight: 700;
    cursor: pointer; transition: all .2s; font-family: 'DM Sans', sans-serif;
  }
  .ac-btn-contact:hover { box-shadow: 0 4px 16px var(--gold-glow); }
  .ac-btn-contact:disabled { opacity: .6; cursor: not-allowed; }
  .ac-btn-select {
    flex: 1; padding: 9px; border-radius: 8px; border: none;
    background: var(--indigo-soft); color: var(--indigo); font-size: .78rem;
    font-weight: 600; cursor: pointer; transition: all .2s;
    font-family: 'DM Sans', sans-serif; border: 1px solid rgba(79,110,242,.3);
  }
  .ac-btn-select:hover { background: rgba(79,110,242,.25); }

  /* Nav Arrows */
  .ac-nav-arrows {
    display: flex; align-items: center; gap: 10px;
  }
  .ac-arrow {
    width: 34px; height: 34px; border-radius: 50%; border: 1px solid var(--border);
    background: var(--ink-soft); color: var(--text-dim); cursor: pointer;
    display: flex; align-items: center; justify-content: center; font-size: .8rem;
    transition: all .2s;
  }
  .ac-arrow:hover { border-color: var(--gold); color: var(--gold); }
  .ac-arrow:disabled { opacity: .3; cursor: not-allowed; }

  /* Skeleton Loader */
  .ac-skeleton {
    flex: 0 0 calc(33.33% - 14px);
    background: var(--ink-soft); border: 1px solid var(--border);
    border-radius: 18px; overflow: hidden;
  }
  .ac-skeleton-img { width: 100%; aspect-ratio: 3/4; background: linear-gradient(90deg, rgba(255,255,255,.04) 25%, rgba(255,255,255,.08) 50%, rgba(255,255,255,.04) 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; }
  .ac-skeleton-body { padding: 16px; }
  .ac-skeleton-line { height: 10px; border-radius: 4px; background: rgba(255,255,255,.06); margin-bottom: 8px; animation: shimmer 1.5s infinite; }
  .ac-skeleton-line.wide { width: 70%; }
  .ac-skeleton-line.short { width: 40%; }
  @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }

  /* Empty State */
  .ac-empty {
    padding: 60px 20px; text-align: center; color: var(--text-dim);
    border: 1px dashed rgba(255,255,255,.1); border-radius: 18px;
  }
  .ac-empty-icon { font-size: 2.5rem; margin-bottom: 14px; opacity: .4; }
  .ac-empty h4 { font-family: 'Cormorant Garamond', serif; font-size: 1.1rem; margin-bottom: 6px; color: var(--text); }
  .ac-empty p { font-size: .8rem; }

  /* ── MODAL ── */
  .ac-modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,.7);
    backdrop-filter: blur(6px); z-index: 200;
    display: flex; align-items: center; justify-content: center;
    animation: fadeIn .2s ease;
  }
  @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
  .ac-modal {
    background: var(--ink-soft); border: 1px solid var(--border);
    border-radius: 20px; padding: 32px; max-width: 420px; width: 90%;
    box-shadow: 0 24px 60px rgba(0,0,0,.6);
    animation: slideUp .25s ease;
  }
  @keyframes slideUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
  .ac-modal h3 { font-family: 'Cormorant Garamond', serif; font-size: 1.3rem; margin-bottom: 8px; }
  .ac-modal p { font-size: .83rem; color: var(--text-dim); line-height: 1.6; margin-bottom: 20px; }
  .ac-modal-highlight { color: var(--gold); font-weight: 600; }
  .ac-modal-actions { display: flex; gap: 10px; }
  .ac-modal-cancel {
    flex: 1; padding: 11px; border-radius: 10px; border: 1px solid var(--border);
    background: transparent; color: var(--text-dim); font-size: .85rem;
    cursor: pointer; font-family: 'DM Sans', sans-serif;
  }
  .ac-modal-confirm {
    flex: 1; padding: 11px; border-radius: 10px; border: none;
    background: linear-gradient(135deg, var(--gold), #e8b84b);
    color: var(--ink); font-weight: 700; font-size: .85rem;
    cursor: pointer; font-family: 'DM Sans', sans-serif;
  }

  /* Loading full page */
  .ac-loading-page {
    min-height: calc(100vh - 60px); display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 18px;
  }
  .ac-loading-ring {
    width: 56px; height: 56px; border-radius: 50%;
    border: 3px solid rgba(201,168,76,.2);
    border-top-color: var(--gold);
    animation: spin .9s linear infinite;
  }
  .ac-loading-page h2 { font-family: 'Cormorant Garamond', serif; font-size: 1.3rem; color: var(--text); }
  .ac-loading-page p { font-size: .83rem; color: var(--text-dim); }

  /* Responsive */
  @media (max-width: 900px) {
    .ac-body { grid-template-columns: 1fr; }
    .ac-left { height: auto; position: static; max-height: 480px; }
    .ac-card { flex: 0 0 calc(50% - 10px); }
  }
  @media (max-width: 600px) {
    .ac-header { padding: 14px 16px; }
    .ac-right { padding: 20px 16px; }
    .ac-card { flex: 0 0 85%; }
  }
`;

/* ═══════════════════════════════════════════════════════
   HELPER — parse simple markdown in bot messages
═══════════════════════════════════════════════════════ */
// Chat text (server messages, stored messages, the user's own input) is rendered through
// dangerouslySetInnerHTML. The backend only ever sends markdown (**bold**, newlines, emoji),
// never HTML, so escape everything FIRST: the only HTML that can appear is what the
// replacements below add.
function escapeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function parseMarkdown(text) {
  if (!text || typeof text !== 'string') return text;
  return escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br/>');
}

/* ═══════════════════════════════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════════════════════════════ */
function SkeletonCard() {
  return (
    <div className="ac-skeleton">
      <div className="ac-skeleton-img" />
      <div className="ac-skeleton-body">
        <div className="ac-skeleton-line wide" />
        <div className="ac-skeleton-line short" />
        <div className="ac-skeleton-line" style={{ width: '55%' }} />
      </div>
    </div>
  );
}

function StarRating({ rating }) {
  return (
    <div className="ac-card-stars">
      {[1,2,3,4,5].map(i =>
        i <= rating ? <FaStar key={i} /> : <FaRegStar key={i} style={{ color: 'var(--text-dim)' }} />
      )}
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="ac-msg bot">
      <div className="ac-msg-bubble">
        <div className="ac-typing">
          <span/><span/><span/>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════ */
const ActorsCastingPage = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();

  /* ── State ── */
  const [featureOffset, setFeatureOffset] = useState(0);
  const [aiOffset, setAiOffset] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(['All']);
  const [viewAll, setViewAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeUser, setActiveUser] = useState(0);
  const [users, setUsers] = useState([]);
  const [featuredUsers, setFeaturedUsers] = useState([]);

  // AI Scouting
  const [isCheckingExisting, setIsCheckingExisting] = useState(true);
  const [conversationId, setConversationId] = useState(null);
  const [scoutingReady, setScoutingReady] = useState(false);
  const [aiActors, setAiActors] = useState([]);
  const [setupMode, setSetupMode] = useState('pipeline_prompt');
  const [isSending, setIsSending] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});

  // Manual Input
  const [manualCharName, setManualCharName] = useState('');
  const [manualCharAge, setManualCharAge] = useState('30s');
  const [manualCharGender, setManualCharGender] = useState('Male');
  const [manualCharRole, setManualCharRole] = useState('Lead');
  const [manualGenre, setManualGenre] = useState('');
  const [manualLanguage, setManualLanguage] = useState('hindi');

  // Chat
  const [chatMessages, setChatMessages] = useState([{
    sender: 'bot',
    text: '🎭 Hi! I can scout actors for your project. Would you like me to use the script from your pipeline, or provide character details manually?'
  }]);
  const [isTyping, setIsTyping] = useState(false);

  // Failed request that can be retried: { action: 'pipeline'|'manual'|'select', actor, retryAfter }
  const [retryInfo, setRetryInfo] = useState(null);
  const [retryLeft, setRetryLeft] = useState(0);

  // Contact modal
  const [contactModal, setContactModal] = useState(null); // { actor, linkedUser }

  const chatEndRef = useRef(null);
  const touchStartX = useRef(null);

  /* ── Scroll chat ── */
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, isTyping]);

  /* ── Retry countdown (wall-clock based, so background-tab throttling can't stretch it) ── */
  useEffect(() => {
    if (!retryInfo) { setRetryLeft(0); return undefined; }
    const until = Date.now() + retryInfo.retryAfter * 1000;
    const tick = () => setRetryLeft(Math.max(Math.ceil((until - Date.now()) / 1000), 0));
    tick();
    const id = setInterval(() => {
      tick();
      if (Date.now() >= until) clearInterval(id);
    }, 250);
    return () => clearInterval(id);
  }, [retryInfo]);

  /* ── Init ── */
  useEffect(() => {
    checkProjectPermissions("Casting Assistant");
    getUsers();
  }, []);

  useEffect(() => {
    if (activeUser && projectId) checkExistingScout();
  }, [activeUser, projectId]);

  /* ── HELPERS ── */
  const addUserMsg = (text) => setChatMessages(prev => [...prev, { sender: 'user', text }]);
  const addBotMsg  = (text, delay = 800) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setChatMessages(prev => [...prev, { sender: 'bot', text }]);
    }, delay);
  };
  const replaceBotMsg = (text) => {
    setIsTyping(false);
    setChatMessages(prev => {
      const updated = [...prev];
      for (let i = updated.length - 1; i >= 0; i--) {
        if (updated[i].sender === 'bot') { updated[i] = { sender: 'bot', text }; break; }
      }
      return updated;
    });
  };

  const toggleCardExpand = (key) => {
    setExpandedCards(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getProfileImage = (pic) => {
    if (!pic) return 'https://placehold.co/300x400/1c2333/c9a84c?text=No+Photo';
    if (pic.startsWith('http://') || pic.startsWith('https://')) return pic;
    return `${baseUrl}/${pic.replace(/^\/+/, '')}`;
  };

  /* ── Permissions ── */
  async function checkProjectPermissions(page) {
    try {
      // getUserById instead of getUserIDByToken - same single call, but its
      // response also includes user_type at no extra round trip, so the
      // free-tier check below can be skipped entirely for non-students
      // instead of always paying for a /free-tier/status call to learn that.
      const r1 = await fetch(`${baseUrl}/getUserById`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` }
      });
      if (!r1.ok) { navigate("/login"); return; }
      const { user_id, user_type } = await r1.json();
      setActiveUser(user_id);

      const r2 = await fetch(`${baseUrl}/getMembersByProjectId`, { headers: { project_id: projectId } });
      const members = (await r2.json()).map(m => m.user_id);
      if (!members.includes(user_id)) { window.alert("No access to this project!"); navigate("/"); return; }

      // Free-tier bypass: a currently-eligible verified student skips the
      // showTools-based check below entirely - checked independently via
      // GET /free-tier/status (same pattern StudentToolSelector.jsx already
      // uses) rather than trusted from navigation state. Only called for
      // user_type === 'student' - a non-student never pays for this extra
      // round trip at all. Anyone else (non-student, or a student who isn't
      // currently eligible - expired, etc.) falls through to the exact
      // existing check below, unchanged.
      if (user_type === 'student') {
        const freeTierRes = await fetch(`${baseUrl}/free-tier/status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` }
        });
        if (freeTierRes.ok) {
          const freeTierStatus = await freeTierRes.json();
          if (freeTierStatus.state === 'active' || freeTierStatus.state === 'not_started') {
            return;
          }
        }
      }

      const r3 = await fetch(`${baseUrl}/showTools`, { headers: { project_id: projectId } });
      const toolItems = await r3.json();
      const toolNames = await Promise.all(toolItems.map(async t => {
        const rt = await fetch(`${baseUrl}/toolData`, { headers: { tool_id: t.tool_id } });
        const td = await rt.json(); return td.name;
      }));
      if (!toolNames.includes(page)) { window.alert("No access to this tool!"); navigate("/services"); }
    } catch (err) { console.error('Permission error:', ); }
  }

  async function getUsers() {
    try {
      const res = await fetch(`${baseUrl}/getEnthusiasts`);
      const data = await res.json();
      const featured = [];
      data.forEach(el => {
        el.phone_number = el.phone_number?.slice(0,3)+'-'+el.phone_number?.slice(3,6)+'-'+el.phone_number?.slice(6,10);
        if (el.birthday) {
          const today = new Date(); const b = new Date(el.birthday);
          let age = today.getFullYear() - b.getFullYear();
          if (today.getMonth() < b.getMonth() || (today.getMonth()===b.getMonth()&&today.getDate()<b.getDate())) age--;
          el.age = age;
        }
        if (el.rating > 2) { const t = { ...el, user_id: 'f'+el.user_id }; featured.push(t); }
      });
      setFeaturedUsers(featured);
      setUsers(data);
    } catch (err) { console.error('getUsers error:'); }
  }

  /* ── Existing Scout Check ── */
  async function checkExistingScout() {
    setIsCheckingExisting(true);
    try {
      const res = await fetch(
        `${baseUrl}/actorConversation/getConversationHistory?project_id=${projectId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
      );
      if (!res.ok) throw new Error();
      const { conversations = [] } = await res.json();
      if (conversations.length > 0) await loadActorConversation(conversations[0].id);
      else setIsCheckingExisting(false);
    } catch { setIsCheckingExisting(false); }
  }

  async function loadActorConversation(convId) {
    try {
      const res = await fetch(
        `${baseUrl}/actorConversation/getConversation?conversation_id=${convId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      const msgs = data.messages.map(m => ({
        sender: m.message_type === 'user' ? 'user' : 'bot',
        text: m.content
      }));
      if (msgs.length) setChatMessages(msgs);
      const latestAI = [...data.messages].reverse().find(m => m.message_type==='ai' && m.actors_data?.actors);
      if (latestAI?.actors_data?.actors) setAiActors(latestAI.actors_data.actors);
      setConversationId(convId);
      setScoutingReady(true);
      setSetupMode('done');
    } catch { console.warn('[casting] conversation load failed'); }
    finally { setIsCheckingExisting(false); }
  }

  /* ── AI Scouting ── */
  // Every request below goes through this. It never throws and never exposes server or
  // JavaScript error text: a failure comes back as a fixed-copy object from friendlyError.js.
  async function callApi(url, options) {
    let res;
    try {
      res = await fetch(url, options);
    } catch (err) {
      return { failure: await toFriendlyError({ err }) };   // offline, or aborted (timeout)
    }
    if (!res.ok) return { failure: await toFriendlyError({ res }) };
    try {
      return { data: await res.json() };
    } catch (err) {
      return { failure: await toFriendlyError({ err }) };   // 200 with a broken body, or aborted mid-body
    }
  }

  // Shows the fixed message for a failure kind, and offers Retry when the kind allows it.
  function showFailure(failure, { action, actor, manualHint = false }) {
    const { kind, message, canRetry, retryAfter } = failure;
    const prefix = kind === 'busy' ? '⏳ ' : '';              // no icon for other kinds; the code is never shown
    replaceBotMsg(`${prefix}${message}${manualHint ? ' Try providing character details manually instead.' : ''}`);
    setRetryInfo(canRetry ? { action, actor, retryAfter } : null);
  }

  function handleRetry() {
    const info = retryInfo;
    if (!info || retryLeft > 0) return;
    setRetryInfo(null);
    if (info.action === 'pipeline') handlePipelineChoice(true);
    else if (info.action === 'manual') handleManualSubmit(true);
    else if (info.action === 'select') handleActorSelect(info.actor, true);
  }

  // `retry === true` (strict) on purpose: these are also used as onClick handlers,
  // which pass the click event as the first argument.
  async function handlePipelineChoice(retry) {
    if (retry !== true) addUserMsg('✅ Use the pipeline script');
    setRetryInfo(null);
    setIsTyping(true);
    setIsSending(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000);
    let failure = null;
    try {
      const params = new URLSearchParams({ project_id: projectId, character_index: 0 });
      const result = await callApi(`${baseUrl}/actorConversation/generateFromPipeline?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        signal: controller.signal
      });
      if (result.failure) failure = result.failure;
      else onScoutingComplete(result.data);
    } catch {
      failure = await toFriendlyError({});                   // unexpected UI-side error: fixed copy only
    } finally { clearTimeout(timeoutId); setIsSending(false); }
    if (failure) {
      showFailure(failure, { action: 'pipeline', manualHint: true });
      setSetupMode('manual_input');                          // still falls back to manual entry
    }
  }

  function handleManualChoice() {
    addUserMsg("🔧 I'll provide character details manually");
    addBotMsg("Sure! Fill in the character details below and I'll find the best matching actors.");
    setSetupMode('manual_input');
  }

  async function handleManualSubmit(retry) {
    if (!manualCharName.trim() || !manualGenre.trim()) { alert('Please fill character name and genre'); return; }
    if (retry !== true) addUserMsg(`🎬 ${manualCharName} · ${manualCharAge} · ${manualCharGender} · ${manualCharRole}\nGenre: ${manualGenre} | Language: ${manualLanguage}`);
    setRetryInfo(null);
    setIsTyping(true);
    setIsSending(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000);
    let failure = null;
    try {
      const fd = new FormData();
      fd.append('project_id', projectId);
      fd.append('character_name', manualCharName.trim());
      fd.append('character_age', manualCharAge);
      fd.append('character_gender', manualCharGender);
      fd.append('character_role', manualCharRole);
      fd.append('genre', manualGenre.trim());
      fd.append('language', manualLanguage);
      if (conversationId) fd.append('conversation_id', conversationId);
      const result = await callApi(`${baseUrl}/actorConversation/generateFromManual`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        body: fd,
        signal: controller.signal
      });
      if (result.failure) failure = result.failure;
      else onScoutingComplete(result.data);
    } catch {
      failure = await toFriendlyError({});                   // unexpected UI-side error: fixed copy only
    } finally {
      clearTimeout(timeoutId);
      setIsSending(false);
    }
    if (failure) {
      showFailure(failure, { action: 'manual' });            // form values are kept so Retry works
    } else {
      setManualCharName(''); setManualCharAge('30s'); setManualCharGender('Male');
      setManualCharRole('Lead'); setManualGenre('');
    }
  }

  function onScoutingComplete(result) {
    setConversationId(result.conversation_id);
    setAiActors(result.actors || []);
    setScoutingReady(true);
    setSetupMode('done');
    setAiOffset(0);
    replaceBotMsg(result.message_content);
  }

  async function handleActorSelect(actor, retry) {
    if (!conversationId) { alert('Please scout actors first'); return; }
    if (retry !== true) addUserMsg(`📍 Selected: ${actor.name}`);
    setRetryInfo(null);
    setIsTyping(true);
    setIsLoadingDetails(true);
    let failure = null;
    try {
      const result = await callApi(`${baseUrl}/actorConversation/selectActor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        body: JSON.stringify({
          conversation_id: conversationId,
          actor_name: actor.name,
          character_name: actor.character_fit || 'Character',
          estimated_fee: actor.estimated_fee_inr || 0,
          current_casting_budget: 0
        })
      });
      if (result.failure) failure = result.failure;
      else replaceBotMsg(result.data.message_content);
    } catch {
      failure = await toFriendlyError({});                   // unexpected UI-side error: fixed copy only
    } finally { setIsLoadingDetails(false); }
    if (failure) showFailure(failure, { action: 'select', actor });
  }

  /* ── Contact Flow ── */
  function handleContactClick(actor) {
    // Try to find a linked user by matching name
    const actorNameLower = actor.name?.toLowerCase() || '';
    const linked = users.find(u => {
      const full = ((u.first_name || '') + ' ' + (u.last_name || '')).trim().toLowerCase();
      const uname = (u.username || '').toLowerCase();
      return full.includes(actorNameLower.split(' ')[0]) || actorNameLower.includes(uname);
    });

    if (linked) {
      setContactModal({ actor, linkedUser: linked });
    } else {
      // No linked user — ask AI for contact info
      handleActorSelect(actor);
      addBotMsg(`🔍 No platform account found for **${actor.name}**. Generating suggested contact info...`, 400);
    }
  }

  function confirmContact(linkedUser) {
    setContactModal(null);
    navigate(`/messenger/${String(linkedUser.user_id).replace('f', '')}`);
  }

  /* ── Filter helpers ── */
  const filterAndSearch = (items) => {
    let r = selectedCategories.includes('All') ? items : items.filter(i => selectedCategories.includes(i.category));
    if (searchTerm) r = r.filter(i =>
      [i.name, i.first_name, i.last_name, i.username].some(s => s?.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    return r;
  };

  const handleCategoryToggle = (cat) => {
    setSelectedCategories(prev => {
      if (cat === 'All') return ['All'];
      const selected = prev.includes(cat)
        ? prev.filter(c => c !== cat)
        : [...prev.filter(c => c !== 'All'), cat];
      return selected.length ? selected : ['All'];
    });
  };

  /* ── Carousel helpers ── */
  const CARDS_VISIBLE = 3;

  /* ── Touch swipe ── */
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e, setOffset, offset, total) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > 50 && offset + CARDS_VISIBLE < total) setOffset(o => o + 1);
    if (diff < -50 && offset > 0) setOffset(o => o - 1);
  };

  /* ═══════════════════════════════════════════════════════
     LOADING STATE
  ═══════════════════════════════════════════════════════ */
  if (isCheckingExisting) {
    return (
      <>
        <style>{STYLES}</style>
        <SubNav />
        <div className="ac-root">
          <div className="ac-loading-page">
            <div className="ac-loading-ring" />
            <h2>Loading your casting workspace…</h2>
            <p>Checking for existing scouting sessions</p>
          </div>
        </div>
      </>
    );
  }

  /* ═══════════════════════════════════════════════════════
     CATEGORIES
  ═══════════════════════════════════════════════════════ */
  const categories = ['All', 'Lead Actor', 'Side Actor', 'Production assistant'];
  const allCategories = [
    'All','Lead Actor','Side Actor','Production assistant',
    'Art directors','Line producers','Camera operators','Production designer',
    'Comedian','Assistant director','Editor','Sound enginner',
    'Makeup artist','Stunt coordinator'
  ];

  const filteredFeatured = filterAndSearch(featuredUsers).filter(u => u.user_id !== activeUser && u.user_id !== 'f'+activeUser);
  const filteredSuggestions = filterAndSearch(users).filter(u => u.user_id !== activeUser);
  const aiSlice = aiActors.slice(aiOffset, aiOffset + CARDS_VISIBLE);
  const featSlice = filteredFeatured.slice(featureOffset, featureOffset + CARDS_VISIBLE);

  /* ═══════════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════════ */
  return (
    <>
      <SEO
        title="Actors Casting"
        description="Find and cast actors for your film project using ProducerBuddy's AI-powered casting search and audition management tools."
        url="https://producerbuddy.pilomation.com/actors-casting"
        robots="noindex, nofollow"
      />
      <style>{STYLES}</style>
      <SubNav />
      <div className="ac-root">

        {/* ── HEADER ── */}
        <header className="ac-header">
          <h1>
            <MdMovieFilter size={22} style={{ color: 'var(--gold)' }} />
            Actors <span>&amp;</span> Casting
          </h1>
          <div className="ac-header-actions">
            {/* Search */}
            <div className="ac-search-bar">
              <FaSearch size={12} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Search actors, crew…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            {/* Filter */}
            <div style={{ position: 'relative' }}>
              <button className="ac-filter-btn" onClick={() => setIsDropdownOpen(o => !o)}>
                <FaSort size={11} /> Filter
              </button>
              {isDropdownOpen && (
                <div className="ac-filter-dropdown">
                  {(viewAll ? allCategories : categories).map(cat => (
                    <label key={cat} className="ac-filter-label">
                      <input type="checkbox" checked={selectedCategories.includes(cat)} onChange={() => handleCategoryToggle(cat)} />
                      {cat}
                    </label>
                  ))}
                  <div className="ac-view-all-btn" onClick={() => setViewAll(v => !v)}>
                    {viewAll ? 'Show fewer' : 'View all categories'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* ── BODY ── */}
        <div className="ac-body">

          {/* ════════════════════════════
              LEFT — AI Scout Chat
          ════════════════════════════ */}
          <aside className="ac-left">
            {/* Panel Header */}
            <div className="ac-left-header">
              <div className="icon-wrap"><FaRobot size={15} /></div>
              <div className="ac-left-header-text">
                <h3>Actor Scout AI</h3>
                <p>Powered by Pilomation Intelligence</p>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="ac-chat-area">
              {chatMessages.map((msg, i) => (
                <div key={i} className={`ac-msg ${msg.sender}`}>
                  <div
                    className="ac-msg-bubble"
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.text) }}
                  />
                </div>
              ))}
              {isTyping && <TypingIndicator />}
              <div ref={chatEndRef} />
            </div>

            {/* ── Controls ── */}

            {/* Retry after a busy / network / server failure (disabled while the countdown runs) */}
            {retryInfo && !isSending && !isLoadingDetails && (
              <div className="ac-choice-btns">
                <button
                  className="ac-choice-btn ac-choice-primary"
                  onClick={handleRetry}
                  disabled={retryLeft > 0}
                >
                  {retryLeft > 0 ? `Retry in ${retryLeft}s` : 'Retry'}
                </button>
              </div>
            )}

            {/* Option A: Choose Pipeline vs Manual */}
            {setupMode === 'pipeline_prompt' && !isSending && (
              <div className="ac-choice-btns">
                <button className="ac-choice-btn ac-choice-primary" onClick={handlePipelineChoice}>
                  ✅ Use Pipeline Script
                </button>
                <button className="ac-choice-btn ac-choice-secondary" onClick={handleManualChoice}>
                  🔧 Provide Details Manually
                </button>
              </div>
            )}

            {/* Option B: Manual Form */}
            {setupMode === 'manual_input' && (
              <div className="ac-manual-form">
                <input className="ac-input" placeholder="Character Name *" value={manualCharName} onChange={e => setManualCharName(e.target.value)} disabled={isSending} />
                <div className="ac-input-row">
                  <input className="ac-input" placeholder="Age (e.g. 30s)" value={manualCharAge} onChange={e => setManualCharAge(e.target.value)} disabled={isSending} />
                  <select className="ac-select" value={manualCharGender} onChange={e => setManualCharGender(e.target.value)} disabled={isSending}>
                    <option>Male</option><option>Female</option><option value="Any">Any Gender</option>
                  </select>
                </div>
                <div className="ac-input-row">
                  <select className="ac-select" value={manualCharRole} onChange={e => setManualCharRole(e.target.value)} disabled={isSending}>
                    <option value="Lead">Lead Role</option>
                    <option value="Supporting">Supporting</option>
                    <option value="Side">Side Character</option>
                  </select>
                  <select className="ac-select" value={manualLanguage} onChange={e => setManualLanguage(e.target.value)} disabled={isSending}>
                    <option value="hindi">Hindi</option><option value="tamil">Tamil</option>
                    <option value="telugu">Telugu</option><option value="malayalam">Malayalam</option>
                    <option value="kannada">Kannada</option><option value="bengali">Bengali</option>
                    <option value="marathi">Marathi</option><option value="punjabi">Punjabi</option>
                  </select>
                </div>
                <input className="ac-input" placeholder="Genre * (e.g. Thriller, Drama)" value={manualGenre} onChange={e => setManualGenre(e.target.value)} disabled={isSending} />
                <button
                  className="ac-scout-btn"
                  onClick={handleManualSubmit}
                  disabled={isSending || !manualCharName.trim() || !manualGenre.trim()}
                >
                  {isSending ? '🔄 Scouting…' : '🎬 Scout Actors'}
                </button>
              </div>
            )}

            {/* Sending Spinner */}
            {isSending && (
              <div className="ac-sending-indicator">
                <div className="ac-spin" />
                Scouting actors across the industry…
              </div>
            )}
          </aside>

          {/* ════════════════════════════
              RIGHT — Actor Cards
          ════════════════════════════ */}
          <main className="ac-right">

            {/* ── AI Scouted Actors ── */}
            {scoutingReady && (
              <section className="ac-section">
                <div className="ac-section-header">
                  <h2 className="ac-section-title">
                    🎭 AI Scouted Actors
                    {aiActors.length > 0 && <span className="badge">{aiActors.length} matches</span>}
                  </h2>
                  {aiActors.length > CARDS_VISIBLE && (
                    <div className="ac-nav-arrows">
                      <button className="ac-arrow" disabled={aiOffset === 0} onClick={() => setAiOffset(o => Math.max(0, o-1))}>
                        <FaArrowLeft size={11} />
                      </button>
                      <span style={{ fontSize: '.75rem', color: 'var(--text-dim)' }}>
                        {aiOffset+1}–{Math.min(aiOffset+CARDS_VISIBLE, aiActors.length)} / {aiActors.length}
                      </span>
                      <button className="ac-arrow" disabled={aiOffset + CARDS_VISIBLE >= aiActors.length} onClick={() => setAiOffset(o => o+1)}>
                        <FaArrowRight size={11} />
                      </button>
                    </div>
                  )}
                </div>

                {isSending ? (
                  <div className="ac-carousel">
                    <SkeletonCard/><SkeletonCard/><SkeletonCard/>
                  </div>
                ) : aiActors.length > 0 ? (
                  <div className="ac-carousel-wrap"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={e => handleTouchEnd(e, setAiOffset, aiOffset, aiActors.length)}
                  >
                    <div className="ac-carousel">
                      <div className="ac-carousel-inner" style={{ transform: `translateX(-${aiOffset * (100/CARDS_VISIBLE + 2.2)}%)` }}>
                        {aiActors.map((actor, i) => {
                          const key = 'ai-' + i;
                          const expanded = !!expandedCards[key];
                          return (
                            <div key={key} className="ac-card">
                              <div className="ac-card-img-wrap">
                                <img
                                  src={actor.profile_image || `https://placehold.co/300x400/1c2333/c9a84c?text=${encodeURIComponent(actor.name?.split(' ')[0]||'Actor')}`}
                                  alt={actor.name}
                                  onError={e => { e.target.src='https://placehold.co/300x400/1c2333/c9a84c?text=Actor'; }}
                                />
                                <div className="ac-card-img-overlay" />
                                <div className="ac-card-score">⭐ {actor.suitability_score}/10</div>
                                {actor.market_tier && <div className="ac-card-tier">{actor.market_tier}</div>}
                              </div>
                              <div className="ac-card-body">
                                <div className="ac-card-name">{actor.name}</div>
                                <div className="ac-card-meta">
                                  {actor.age && <span className="ac-card-tag">{actor.age}</span>}
                                  {actor.gender && <span className="ac-card-tag">{actor.gender}</span>}
                                  {actor.based_in && <span className="ac-card-tag">📍 {actor.based_in}</span>}
                                </div>
                                <div className={`ac-card-details ${expanded ? 'open' : ''}`}>
                                  {actor.estimated_fee_inr && (
                                    <div className="ac-card-detail-row">
                                      <span className="label">Est. Fee</span>
                                      <span className="val" style={{ color: 'var(--gold)' }}>₹{actor.estimated_fee_inr?.toLocaleString()}</span>
                                    </div>
                                  )}
                                  {actor.languages?.length > 0 && (
                                    <div className="ac-card-detail-row">
                                      <span className="label">Languages</span>
                                      <span className="val">{actor.languages.join(', ')}</span>
                                    </div>
                                  )}
                                  {actor.budget_compatible !== undefined && (
                                    <div className="ac-card-detail-row">
                                      <span className="label">Budget Fit</span>
                                      <span className="val">{actor.budget_compatible ? '✅ Compatible' : '⚠️ Over Budget'}</span>
                                    </div>
                                  )}
                                  {actor.character_fit && (
                                    <div className="ac-card-detail-row" style={{ flexDirection: 'column', gap: 4 }}>
                                      <span className="label">Why Perfect</span>
                                      <span className="val" style={{ textAlign:'left', maxWidth:'100%', fontSize:'.72rem', color:'var(--text-dim)' }}>
                                        {actor.character_fit.substring(0, 120)}…
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="ac-card-actions">
                                  <button className="ac-btn-view" onClick={() => toggleCardExpand(key)}>
                                    {expanded ? 'Less' : 'Details'}
                                  </button>
                                  <button
                                    className="ac-btn-contact"
                                    onClick={() => handleContactClick(actor)}
                                    disabled={isLoadingDetails}
                                  >
                                    Contact
                                  </button>
                                  <button
                                    className="ac-btn-select"
                                    onClick={() => handleActorSelect(actor)}
                                    disabled={isLoadingDetails}
                                  >
                                    {isLoadingDetails ? '…' : 'Scout'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="ac-empty">
                    <div className="ac-empty-icon">🎬</div>
                    <h4>No actors scouted yet</h4>
                    <p>Use the AI panel on the left to begin scouting.</p>
                  </div>
                )}
              </section>
            )}

            {/* ── Featured Actors ── */}
            <section className="ac-section">
              <div className="ac-section-header">
                <h2 className="ac-section-title">
                  ⭐ Featured Actors
                  {filteredFeatured.length > 0 && <span className="badge">{filteredFeatured.length} profiles</span>}
                </h2>
                {filteredFeatured.length > CARDS_VISIBLE && (
                  <div className="ac-nav-arrows">
                    <button className="ac-arrow" disabled={featureOffset === 0} onClick={() => setFeatureOffset(o => Math.max(0, o-1))}>
                      <FaArrowLeft size={11} />
                    </button>
                    <span style={{ fontSize: '.75rem', color: 'var(--text-dim)' }}>
                      {featureOffset+1}–{Math.min(featureOffset+CARDS_VISIBLE, filteredFeatured.length)} / {filteredFeatured.length}
                    </span>
                    <button className="ac-arrow" disabled={featureOffset+CARDS_VISIBLE >= filteredFeatured.length} onClick={() => setFeatureOffset(o => o+1)}>
                      <FaArrowRight size={11} />
                    </button>
                  </div>
                )}
              </div>

              {filteredFeatured.length === 0 ? (
                <div className="ac-empty">
                  <div className="ac-empty-icon"><FaUser /></div>
                  <h4>No featured actors found</h4>
                  <p>Try adjusting your search or filters.</p>
                </div>
              ) : (
                <div className="ac-carousel-wrap"
                  onTouchStart={handleTouchStart}
                  onTouchEnd={e => handleTouchEnd(e, setFeatureOffset, featureOffset, filteredFeatured.length)}
                >
                  <div className="ac-carousel">
                    <div className="ac-carousel-inner" style={{ transform: `translateX(-${featureOffset * (100/CARDS_VISIBLE + 2.2)}%)` }}>
                      {filteredFeatured.map(item => {
                        const key = 'feat-' + item.user_id;
                        const expanded = !!expandedCards[key];
                        return (
                          <div key={item.user_id} className="ac-card">
                            <div className="ac-card-img-wrap">
                              <img
                                src={getProfileImage(item.profile_picture)}
                                alt={item.first_name || item.username}
                                onError={e => { e.target.src='https://placehold.co/300x400/1c2333/c9a84c?text=No+Photo'; }}
                              />
                              <div className="ac-card-img-overlay" />
                            </div>
                            <div className="ac-card-body">
                              <div className="ac-card-name">
                                {item.first_name ? `${item.first_name} ${item.last_name}` : item.username}
                              </div>
                              <StarRating rating={item.rating} />
                              <div className="ac-card-meta">
                                {item.category && <span className="ac-card-tag">{item.category}</span>}
                                {item.gender && <span className="ac-card-tag">{item.gender}</span>}
                              </div>
                              <div className={`ac-card-details ${expanded ? 'open' : ''}`}>
                                {item.first_name && (
                                  <div className="ac-card-detail-row">
                                    <span className="label">Username</span><span className="val">{item.username}</span>
                                  </div>
                                )}
                                <div className="ac-card-detail-row">
                                  <span className="label">Age</span>
                                  <span className="val">{item.birthday ? item.age : '—'}</span>
                                </div>
                                <div className="ac-card-detail-row">
                                  <span className="label">Experience</span>
                                  <span className="val">{item.year_experience} yr{item.year_experience !== 1 ? 's' : ''}</span>
                                </div>
                                <div className="ac-card-detail-row">
                                  <span className="label">Mobile</span><span className="val">{item.phone_number}</span>
                                </div>
                                <div className="ac-card-detail-row">
                                  <span className="label">Remuneration</span><span className="val">Contact for details</span>
                                </div>
                              </div>
                              <div className="ac-card-actions">
                                <button className="ac-btn-view" onClick={() => toggleCardExpand(key)}>
                                  {expanded ? 'Less' : 'Details'}
                                </button>
                                <button
                                  className="ac-btn-contact"
                                  onClick={() => navigate('/messenger/' + String(item.user_id).replace('f', ''))}
                                >
                                  Book
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* ── All Suggestions ── */}
            {/* <section className="ac-section">
              <div className="ac-section-header">
                <h2 className="ac-section-title">
                  <FaFilm size={14} style={{ color: 'var(--gold)' }} />
                  Suggestions
                  {filteredSuggestions.length > 0 && <span className="badge">{filteredSuggestions.length}</span>}
                </h2>
              </div>
              {filteredSuggestions.length === 0 ? (
                <div className="ac-empty">
                  <div className="ac-empty-icon"><FaSearch /></div>
                  <h4>No suggestions match your filter</h4>
                  <p>Try clearing your search or selecting 'All'.</p>
                </div>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:'20px' }}>
                  {filteredSuggestions.map(item => {
                    const key = 'sug-' + item.user_id;
                    const expanded = !!expandedCards[key];
                    return (
                      <div key={item.user_id} className="ac-card">
                        <div className="ac-card-img-wrap">
                          <img
                            src={getProfileImage(item.profile_picture)}
                            alt={item.first_name || item.username}
                            onError={e => { e.target.src='https://placehold.co/300x400/1c2333/c9a84c?text=No+Photo'; }}
                          />
                          <div className="ac-card-img-overlay" />
                        </div>
                        <div className="ac-card-body">
                          <div className="ac-card-name">
                            {item.first_name ? `${item.first_name} ${item.last_name}` : item.username}
                          </div>
                          <StarRating rating={item.rating} />
                          <div className="ac-card-meta">
                            {item.category && <span className="ac-card-tag">{item.category}</span>}
                          </div>
                          <div className={`ac-card-details ${expanded ? 'open' : ''}`}>
                            <div className="ac-card-detail-row">
                              <span className="label">Age</span><span className="val">{item.birthday ? item.age : '—'}</span>
                            </div>
                            <div className="ac-card-detail-row">
                              <span className="label">Gender</span><span className="val">{item.gender || '—'}</span>
                            </div>
                            <div className="ac-card-detail-row">
                              <span className="label">Experience</span><span className="val">{item.year_experience} yrs</span>
                            </div>
                            <div className="ac-card-detail-row">
                              <span className="label">Mobile</span><span className="val">{item.phone_number}</span>
                            </div>
                          </div>
                          <div className="ac-card-actions">
                            <button className="ac-btn-view" onClick={() => toggleCardExpand(key)}>
                              {expanded ? 'Less' : 'Details'}
                            </button>
                            <button
                              className="ac-btn-contact"
                              onClick={() => navigate('/messenger/' + String(item.user_id).replace('f', ''))}
                            >
                              Book
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section> */}

          </main>
        </div>

        {/* ═══════════════════════════════════════════════════════
            CONTACT CONFIRMATION MODAL
        ═══════════════════════════════════════════════════════ */}
        {contactModal && (
          <div className="ac-modal-overlay" onClick={() => setContactModal(null)}>
            <div className="ac-modal" onClick={e => e.stopPropagation()}>
              <h3>Scout this Actor?</h3>
              <p>
                A platform account was found for{' '}
                <span className="ac-modal-highlight">{contactModal.actor.name}</span>.
                Would you like to reach out and scout them for this project?
              </p>
              <div className="ac-modal-actions">
                <button className="ac-modal-cancel" onClick={() => setContactModal(null)}>Cancel</button>
                <button className="ac-modal-confirm" onClick={() => confirmContact(contactModal.linkedUser)}>
                  Yes, Contact Now →
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

// The page is wrapped so a render crash shows a fixed message and a Refresh button
// instead of a blank screen or error text.
const ActorsCasting = () => (
  <CastingErrorBoundary>
    <ActorsCastingPage />
  </CastingErrorBoundary>
);

export default ActorsCasting;
